import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isPast } from 'date-fns';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Props as LoadingButtonProps } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import {
  IOsAppFeature,
  NotIOsAppFeature,
} from '@sorare/core/src/components/BuyableFeature';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useLoggedCallback } from '@sorare/core/src/hooks/useLoggedCallback';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useTokenOfferBelongsToUser } from '@sorare/core/src/hooks/useTokenOfferBelongsToUser';
import { glossary, mobileApp } from '@sorare/core/src/lib/glossary';

import { BuyPaymentFlow } from '#src/components/buyActions/BuyPaymentFlow/index.tsx';
import { CancelOffer } from '#src/components/buyActions/CancelOffer/index.tsx';
import { useClickBuyEvent } from '#src/hooks/events/useClickBuyEvent.ts';
import { useCannotBuy } from '#src/hooks/offers/useCannotBuy.ts';
import { useCannotSettleInCrypto } from '#src/hooks/offers/useCannotSettleInCrypto.ts';
import { useCannotSettleInFiat } from '#src/hooks/offers/useCannotSettleInFiat.ts';

import type { BuyField_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  color?: LoadingButtonProps['color'];
  cancelColor?: LoadingButtonProps['color'];
  card: BuyField_anyCard;
  fullWidth?: boolean;
  buttonLabel?: ReactNode;
  renderButton?: (props: Partial<LoadingButtonProps>) => ReactNode;
  variant: 'small' | 'medium';
};

const Buy = styled(LoadingButton)`
  flex-shrink: 0;
`;

const BuyTooltip = styled(Tooltip)`
  &.fullWidth {
    width: 100%;
  }
`;

export const BuyField = ({
  card,
  variant,
  color = 'primary',
  fullWidth = false,
  buttonLabel = <FormattedMessage {...glossary.buyNow} />,
  renderButton,
}: Props) => {
  const {
    isMarketAuthorizedIosApp,
    postMessage,
    canOpenCartOpenPaymentBoxAndCloseCurrentWebview,
  } = useIsMobileApp();
  const canOpenPaymentBox =
    isMarketAuthorizedIosApp && canOpenCartOpenPaymentBoxAndCloseCurrentWebview;

  const belongsToUser = useTokenOfferBelongsToUser();
  const cannotBuy = useCannotBuy();
  const cannotSettleInFiat = useCannotSettleInFiat();
  const cannotSettleInCrypto = useCannotSettleInCrypto();
  const { cryptocurrencySymbol } = useCurrentUserCurrencies();
  const {
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const [paymentStarted, setPaymentStarted] = useState<boolean>(false);
  const loggedTogglePaymentStarted = useLoggedCallback<boolean>(b => {
    if (canOpenPaymentBox) {
      postMessage('openPaymentBox');
    }
    setPaymentStarted(b);
  });

  const trackClickBuy = useClickBuyEvent();

  const { liveSingleSaleOffer, myMintedSingleSaleOffer } = card;

  const cannotBuyToken = cannotBuy(card);
  const cannotBuyTokenWithFiat = cannotSettleInFiat(card);
  const cannotBuyTokenWithCrypto = cannotSettleInCrypto(card);
  const disableBuyButtonMessages = [
    cannotBuyToken,
    cannotBuyTokenWithFiat,
    onlyShowFiatCurrency ? null : cannotBuyTokenWithCrypto,
  ].filter(Boolean);

  const disableBuyButton = (() => {
    if (cannotBuyToken) return true;
    if (cannotBuyTokenWithFiat && onlyShowFiatCurrency) return true;
    if (cannotBuyTokenWithCrypto && cannotBuyTokenWithFiat) return true;
    return false;
  })();

  // Give the possibility for owner of the card to cancel his offer before it is live
  if (myMintedSingleSaleOffer && belongsToUser(myMintedSingleSaleOffer))
    return (
      <NotIOsAppFeature exceptMarketAuthorizedIosApp>
        <CancelOffer
          color="red"
          variant={variant}
          card={card}
          fullWidth={fullWidth}
        />
      </NotIOsAppFeature>
    );

  // Don't show the offer to users if it is not live yet
  if (!liveSingleSaleOffer) return null;

  const { id, endDate, receiverSide } = liveSingleSaleOffer;
  const { amounts } = receiverSide;

  // Don't show the button if the offer expired
  if (isPast(endDate)) return null;

  const monetaryAmount = toMonetaryAmount(amounts);

  const onClick = () => {
    trackClickBuy(
      id,
      monetaryAmount.wei,
      monetaryAmount.eur,
      [card],
      card.sport
    );
    return loggedTogglePaymentStarted(true);
  };

  return (
    <>
      <BuyTooltip
        className={classNames({ fullWidth })}
        title={
          disableBuyButton
            ? disableBuyButtonMessages.map((message, idx) => (
                <LabelM key={idx}>
                  <FormattedMessage
                    {...message}
                    values={{
                      cryptocurrencySymbol,
                    }}
                  />
                </LabelM>
              ))
            : ''
        }
      >
        <NotIOsAppFeature exceptMarketAuthorizedIosApp>
          {renderButton ? (
            renderButton({
              onClick,
              loading: paymentStarted,
              disabled: disableBuyButton,
            })
          ) : (
            <Buy
              color={color}
              onClick={onClick}
              size={variant}
              loading={paymentStarted}
              disabled={disableBuyButton}
              fullWidth={fullWidth}
            >
              {buttonLabel}
            </Buy>
          )}
        </NotIOsAppFeature>
        <IOsAppFeature exceptMarketAuthorizedIosApp>
          <Buy
            color={color}
            onClick={() => {}}
            size={variant}
            fullWidth={fullWidth}
            loading={false}
            disabled
          >
            <FormattedMessage {...mobileApp.unavailable} />
          </Buy>
        </IOsAppFeature>
      </BuyTooltip>
      {!!paymentStarted && (
        <BuyPaymentFlow
          onClose={() => setPaymentStarted(false)}
          offerId={liveSingleSaleOffer.id}
        />
      )}
    </>
  );
};

BuyField.fragments = {
  anyCard: gql`
    fragment BuyField_anyCard on AnyCardInterface {
      slug
      sport
      liveSingleSaleOffer @skip(if: $onlyPrimary) {
        id
        endDate
        receiverSide {
          id
          amounts {
            ...useMonetaryAmount_monetaryAmount
          }
        }
      }
      myMintedSingleSaleOffer @skip(if: $onlyPrimary) {
        id
        ...useTokenOfferBelongsToUser_offer
      }
      ...CancelOffer_anyCard
      ...useCannotBuy_anyCard
      ...useCannotSettleInFiat_anyCard
      ...useCannotSettleInCrypto_anyCard
      ...useClickBuyEvent_anyCard
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${useTokenOfferBelongsToUser.fragments.offer}
    ${CancelOffer.fragments.anyCard}
    ${useCannotBuy.fragments.anyCard}
    ${useCannotSettleInFiat.fragments.anyCard}
    ${useCannotSettleInCrypto.fragments.anyCard}
    ${useClickBuyEvent.fragments.anyCard}
  ` as TypedDocumentNode<BuyField_anyCard>,
};
